import {createSlice} from '@reduxjs/toolkit';

import deviceNavigationMenuTypes from '../../consts/home/deviceNavigationMenuTypes';
import storeSliceNames from '../../consts/storeSliceNames';
import storageService from '../../services/storageService';

const initialState = {
    isDevicesInitialized: false,
    consents: [],
    consentsFlagsValues: {},
    initialConnectionType: undefined,
    initialCodentify: undefined,
    initialDeviceType: undefined,
    initialDusn: undefined,
    initialHolderDusn: undefined,
    qrCodentify: undefined,
    isConsentsReceiveInProgress: true,
    isUpdateConsentsInProgress: false,
    isGeoRestrictionEnabled: true,
    captivePortalProgress: undefined,
    activeDeviceNavigationMenuType: deviceNavigationMenuTypes.PERSONALISE,
    languageCode: undefined,
    isPushNotificationPageVisible: false,
    isHomeMenuExpanded: false,
    isUwmFullScreenRequired: false,
    isFirmwareMock: false,
    isUsageConcentAccepted: storageService.getIsUsageConcentAccepted(),
    isExperienceScoreConsentsAccepted: storageService.getIsExperienceConcentAccepted(),
    isIncludeEmptyPuff: false,
    isSubscriptionEnabled: false,
    isUpdateSubscriptionInProgress: false,
    disabledNotificationTypes: storageService.getDisabledNotificationTypes(),
    isUserSessionInitialized: false,
    isSessionValidationInProgress: false,
    isWiFiConfiguringError: false,
    isOnboardingInProgress: false,
};

const sessionSlice = createSlice({
    name: storeSliceNames.session,
    initialState,
    reducers: {
        setIsDevicesInitialized(state, action) {
            state.isDevicesInitialized = action.payload;
        },
        setConsents(state, action) {
            state.consents = action.payload;
        },
        setConsentsFlagsValues(state, action) {
            state.consentsFlagsValues = action.payload;
        },
        clearConsents(state) {
            state.consentsFlagsValues = initialState.consentsFlagsValues;
            state.consents = initialState.consents;
        },
        setInitialConnectionType(state, action) {
            state.initialConnectionType = action.payload;
        },
        setInitialCodentify(state, action) {
            state.initialCodentify = action.payload;
        },
        setInitialDeviceType(state, action) {
            state.initialDeviceType = action.payload;
        },
        setInitialDusn(state, action) {
            state.initialDusn = action.payload;
        },
        setInitialHolderDusn(state, action) {
            state.initialHolderDusn = action.payload;
        },
        setQrCodentify(state, action) {
            state.qrCodentify = action.payload;
        },
        setIsConsentsReceiveInProgress(state, action) {
            state.isConsentsReceiveInProgress = action.payload;
        },
        setIsUpdateConsentsInProgress(state, action) {
            state.isUpdateConsentsInProgress = action.payload;
        },
        setCaptivePortalProgress(state, action) {
            state.captivePortalProgress = action.payload;
        },
        setActiveDeviceNavigationMenuType(state, action) {
            state.activeDeviceNavigationMenuType = action.payload;
        },
        setShouldSetupGuideBeShown(state, action) {
            state.shouldSetupGuideBeShown = action.payload;
        },
        setShouldRedirectToHome(state, action) {
            state.shouldRedirectToHome = action.payload;
        },
        setLanguageCode(state, action) {
            state.languageCode = action.payload;
        },
        setIsFirmwareMock(state, action) {
            state.isFirmwareMock = action.payload;
        },
        setIsPushNotificationPageVisible(state, action) {
            state.isPushNotificationPageVisible = action.payload;
        },
        setIsGeoRestrictionEnabled(state, action) {
            state.isGeoRestrictionEnabled = action.payload;
        },
        setIsHomeMenuExpanded(state, action) {
            state.isHomeMenuExpanded = action.payload;
        },
        setIsUwmFullScreenRequired(state, action) {
            state.isUwmFullScreenRequired = action.payload;
        },
        setIsUsageConcentAccepted(state, action) {
            state.isUsageConcentAccepted = action.payload;
        },
        setIsExperienceScoreConsentsAccepted(state, action) {
            state.isExperienceScoreConsentsAccepted = action.payload;
        },
        setIsSecondWifiConnectionActive(state, action) {
            state.isSecondWifiConnectionActive = action.payload;
        },
        setIsIncludeEmptyPuff(state, action) {
            state.isIncludeEmptyPuff = action.payload;
        },
        setIsUpdateSubscriptionInProgress(state, action) {
            state.isUpdateSubscriptionInProgress = action.payload;
        },
        updateDisabledNotificationTypes(state, action) {
            state.disabledNotificationTypes = action.payload;
        },
        setIsSessionValidationInProgress(state, action) {
            state.isSessionValidationInProgress = action.payload;
        },
        setIsSubscriptionEnabled(state, action) {
            state.isSubscriptionEnabled = action.payload;
        },
        setIsUserSessionInitialized(state, action) {
            state.isUserSessionInitialized = action.payload;
        },
        setIsWiFiConfiguringError(state, action) {
            state.isWiFiConfiguringError = action.payload;
        },
        setIsOnboardingInProgress(state, action) {
            state.isOnboardingInProgress = action.payload;
        },
    },
});

export const setIsDevicesInitialized = (isInitialized) => sessionSlice.actions.setIsDevicesInitialized(isInitialized);

export const setConsents = (items) => sessionSlice.actions.setConsents(items);

export const setConsentsFlagsValues = (consentsFlagsValues) =>
    sessionSlice.actions.setConsentsFlagsValues(consentsFlagsValues);

export const clearConsents = () => sessionSlice.actions.clearConsents();

export const setInitialConnectionType = (type) => sessionSlice.actions.setInitialConnectionType(type);

export const resetInitialConnectionType = () =>
    sessionSlice.actions.setInitialConnectionType(initialState.initialConnectionType);

export const setInitialDeviceType = (deviceType) => sessionSlice.actions.setInitialDeviceType(deviceType);

export const resetInitialDeviceType = () => sessionSlice.actions.setInitialDeviceType(initialState.initialDeviceType);

export const setInitialDusn = (dusn) => sessionSlice.actions.setInitialDusn(dusn);

export const resetInitialDusn = () => sessionSlice.actions.setInitialDusn(initialState.initialDusn);

export const setInitialHolderDusn = (dusn) => sessionSlice.actions.setInitialHolderDusn(dusn);

export const resetInitialHolderDusn = () => sessionSlice.actions.setInitialHolderDusn(initialState.initialHolderDusn);

export const setInitialCodentify = (codentify) => sessionSlice.actions.setInitialCodentify(codentify);

export const setQrCodentify = (codentify) => sessionSlice.actions.setQrCodentify(codentify);

export const setIsConsentsReceiveInProgress = (inProgress) =>
    sessionSlice.actions.setIsConsentsReceiveInProgress(inProgress);

export const setIsUpdateConsentsInProgress = (inProgress) =>
    sessionSlice.actions.setIsUpdateConsentsInProgress(inProgress);

export const setCaptivePortalProgress = (progress) => sessionSlice.actions.setCaptivePortalProgress(progress);

export const resetInitialCodentify = () => sessionSlice.actions.setInitialCodentify(initialState.initialCodentify);

export const resetQrCodentify = () => sessionSlice.actions.setQrCodentify(initialState.qrCodentify);

export const setActiveDeviceNavigationMenuType = (type) => sessionSlice.actions.setActiveDeviceNavigationMenuType(type);

export const resetActiveDeviceNavigationMenuType = () =>
    sessionSlice.actions.setActiveDeviceNavigationMenuType(initialState.activeDeviceNavigationMenuType);

export const setShouldSetupGuideBeShown = (isShown) => sessionSlice.actions.setShouldSetupGuideBeShown(isShown);

export const setShouldRedirectToHome = (value) => sessionSlice.actions.setShouldRedirectToHome(value);

export const setLanguageCode = (languageCode) => sessionSlice.actions.setLanguageCode(languageCode);

export const setIsFirmwareMock = (value) => sessionSlice.actions.setIsFirmwareMock(value);

export const setIsPushNotificationPageVisible = (value) => sessionSlice.actions.setIsPushNotificationPageVisible(value);

export const setIsGeoRestrictionEnabled = (value) => sessionSlice.actions.setIsGeoRestrictionEnabled(value);

export const setIsHomeMenuExpanded = (value) => sessionSlice.actions.setIsHomeMenuExpanded(value);

export const setIsUwmFullScreenRequired = (value) => sessionSlice.actions.setIsUwmFullScreenRequired(value);

export const setIsUsageConcentAccepted = (value) => sessionSlice.actions.setIsUsageConcentAccepted(value);

export const setIsExperienceScoreConsentsAccepted = (value) =>
    sessionSlice.actions.setIsExperienceScoreConsentsAccepted(value);

// Temporary solution until we have a count of connected networks feature
export const setIsSecondWifiConnectionActive = (value) => sessionSlice.actions.setIsSecondWifiConnectionActive(value);

export const setIsIncludeEmptyPuff = (value) => sessionSlice.actions.setIsIncludeEmptyPuff(value);

export const setIsUpdateSubscriptionInProgress = (value) =>
    sessionSlice.actions.setIsUpdateSubscriptionInProgress(value);

export const updateDisabledNotificationTypes = (subTypes) => {
    return sessionSlice.actions.updateDisabledNotificationTypes(subTypes);
};

export const setIsSessionValidationInProgress = (value) => sessionSlice.actions.setIsSessionValidationInProgress(value);

export const setIsSubscriptionEnabled = (value) => sessionSlice.actions.setIsSubscriptionEnabled(value);

export const setIsUserSessionInitialized = (value) => sessionSlice.actions.setIsUserSessionInitialized(value);

export const setIsWiFiConfiguringError = (isError) => sessionSlice.actions.setIsWiFiConfiguringError(isError);

export const setIsOnboardingInProgress = (value) => sessionSlice.actions.setIsOnboardingInProgress(value);

export default sessionSlice.reducer;
